import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Loader from "../../../../components/Common/Loader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import GoogleAutocomplete from "../../../../components/Common/GoogleAutoComplete";
import Select from "react-select";
import { Country, State, City } from "country-state-city";

const getSchema = () =>
  z.object({
    pin: z.string({ required_error: "PIN is required" }),
    country: z.string({ required_error: "Country is required" }),
    state: z.string({ required_error: "State is required" }),
    city: z.string({ required_error: "City is required" }),
    assignTo: z
      .array(z.string())
      .min(1, { message: "At least one hotel must be selected" }),
    status: z.boolean().default(true),
    description: z.string().optional(),
  });

const AddPin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const schema = useMemo(() => getSchema(), []);
  const [pinDetails, setPinDetails] = useState(null);
  const [loader, setLoader] = useState(false);
  const [hotels, setHotels] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: true,
    },
  });

  const selectedCountry = watch("country");
  const selectedState = watch("state");
  const selectedCity = watch("city");

  //  Fetch hotels when city is selected
  useEffect(() => {
    if (selectedCountry && selectedState && selectedCity) {
    
      const fetchHotels = async () => {
        setLoader(true);
       
        const res = await fakeHotelApi(
          selectedCountry,
          selectedState,
          selectedCity
        );
        setHotels(res);
        setLoader(false);
      };
      fetchHotels();
    }
  }, [selectedCountry, selectedState, selectedCity]);

  const fakeHotelApi = async (country, state, city) => {
    return [
      { id: "h1", name: "Hotel Paradise" },
      { id: "h2", name: "Hotel Grand" },
      { id: "h3", name: "City View Hotel" },
    ];
  };

  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      assignTo: data.assignTo.map((h) => h.value), // take selected hotel IDs
      status: data.status ? "active" : "inactive",
      ...(id && { _id: id }),
    };

    // const res = await (id ? UPDATE_PIN(body) : ADD_PIN(body));
    const success = checkResponse({ res: { status: 200 }, showSuccess: true });

    if (success) navigate("/pin");
  });

  return (
    <>
      {loader && <Loader />}
      <section className="addPin py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to="/pin"
                  className="border d-flex align-items-center p-2 rounded"
                >
                    <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      stroke-width="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark">
                  {id ? "Edit PIN" : "Add New PIN"}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrapper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row>
                    <Col lg="6" className="my-2">
                      <label className="form-label">PIN</label>
                      <input
                        type="text"
                        placeholder="Enter PIN"
                        className="form-control"
                        {...register("pin")}
                      />
                      {errors?.pin && (
                        <p className="text-danger">{errors.pin.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>Country</label>
                      <Controller
                        control={control}
                        name="country"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={Country.getAllCountries().map((c) => ({
                              value: c.isoCode,
                              label: c.name,
                            }))}
                            onChange={(option) => {
                              field.onChange(option.label);
                              setValue("state", "");
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>State</label>
                      <Controller
                        control={control}
                        name="state"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={State.getStatesOfCountry(
                              Country.getAllCountries().find(
                                (c) => c.name === selectedCountry
                              )?.isoCode
                            ).map((s) => ({
                              value: s.isoCode,
                              label: s.name,
                            }))}
                            onChange={(option) => {
                              field.onChange(option.label);
                              setValue("city", "");
                            }}
                          />
                        )}
                      />
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>City</label>
                      <Controller
                        control={control}
                        name="city"
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={City.getCitiesOfState(
                              Country.getAllCountries().find(
                                (c) => c.name === selectedCountry
                              )?.isoCode,
                              State.getStatesOfCountry(
                                Country.getAllCountries().find(
                                  (c) => c.name === selectedCountry
                                )?.isoCode
                              ).find((s) => s.name === selectedState)?.isoCode
                            ).map((ci) => ({
                              value: ci.name,
                              label: ci.name,
                            }))}
                            onChange={(option) => field.onChange(option.label)}
                          />
                        )}
                      />
                    </Col>

                    {/* Hotels Multi-select */}
                    <Col lg="12" className="my-2">
                      <label>Assign To Hotels</label>
                      <Controller
                        control={control}
                        name="assignTo"
                        render={({ field }) => (
                          <Select
                            {...field}
                            isMulti
                            options={hotels.map((h) => ({
                              value: h.id,
                              label: h.name,
                            }))}
                            onChange={(option) => field.onChange(option)}
                          />
                        )}
                      />
                      {errors?.assignTo && (
                        <p className="text-danger">{errors.assignTo.message}</p>
                      )}
                    </Col>

                    <Col lg="12" className="my-3">
                      <div className="d-flex align-items-center gap-3 px-4">
                        <Form.Check
                          type="switch"
                          id="status-switch"
                          label="Status"
                          {...register("status")}
                          style={{ transform: "scale(1.4)", cursor: "pointer" }}
                          className="form-switch-success"
                        />
                      </div>
                    </Col>

                    {/* Description */}
                    <Col lg="12" className="my-2">
                      <label className="form-label">
                        Description (optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows={3}
                        {...register("description")}
                      />
                    </Col>

                    <Col
                      lg="12"
                      className="my-3 d-flex gap-3 justify-content-center"
                    >
                      <Button
                        type="button"
                        onClick={() => navigate("/pin")}
                        className="GreyBtn"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="commonBtn">
                        {id ? "Update" : "Submit"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddPin;
